import { MachineDetailsFromAPI, useTonMachines } from '@/contexts/machines'
import { useTonPlans } from '@/contexts/plans'
import { useGetCheckoutLink } from '@/hooks/useGetCheckoutLink'
import { useGetMachinePriceDetails } from '@/hooks/useGetMachinePriceDetails'
import { IPromotionalTag } from '@/types/cms'
import cn from '@/utils/cn'

import RetainQueryLinkDefault from '../RetainQueryLink/RetainQueryLinkDefault'
import DefaultProducts from './components/DefaultProducts'
import Features from './components/Features'

interface TonMachineCardProps extends MachineDetailsFromAPI {
  className?: string
  promotionalTag: IPromotionalTag
}

const TonMachineCard = ({
  id,
  refID,
  className,
  original_price,
  amount,
  discount_percentage,
  promotionalTag
}: TonMachineCardProps) => {
  const { currentPlan } = useTonPlans()
  const { getCheckoutLink } = useGetCheckoutLink()
  const { machinesDetailsByRefID } = useTonMachines()
  const { getMachinePriceDetails } = useGetMachinePriceDetails()

  const machineDetails = machinesDetailsByRefID.get(refID)

  if (!machineDetails || !currentPlan) return null

  const linkToCheckout = getCheckoutLink(id)
  const planPopularName =
    currentPlan.commercial_name.length <= 11
      ? currentPlan.commercial_name
      : currentPlan.short_commercial_name
  const machineImageName = machineDetails.image
  const machinePopularName = machineDetails.name

  const { sellingPrice, origionalPrice, installmentValue, installmentParcel } =
    getMachinePriceDetails({
      id,
      refID,
      amount,
      original_price
    })

  return (
    <div className={cn('bg-display-0 rounded-2xl shadow-new', className)}>
      <div className="flex relative items-end mr-16 ml-24 h-[148px] rounded rounded-b-none">
        <DefaultProducts
          badges={machineDetails.badges}
          machineImageName={machineImageName}
          machinePopularName={machinePopularName}
          discountPercent={discount_percentage}
          promotionalTag={promotionalTag}
        />
      </div>
      <div className="px-16 md:px-24 mt-[24px]">
        <div className="flex justify-between items-center min-w-max">
          <h3 className="text-28 md:text-34 font-bold leading-36 lg:leading-48">
            {machinePopularName}{' '}
            <span className="font-light">{planPopularName}</span>
          </h3>
        </div>
        <div className="flex flex-col justify-between">
          <div className="flex mt-16">
            <div className="mr-8 md:mr-16">
              <p className="paragraph-14">
                <s>R$ {origionalPrice}</s>
              </p>
              <p className="text-newTon-700">
                <b>R$ {sellingPrice}</b>
              </p>
              <p className="paragraph-14">à vista ou</p>
            </div>
            <div className="flex justify-end my-auto ml-auto font-heading font-bold text-newTon-700 paragraph-14 md:paragraph-16">
              <div className="self-end mr-4 text-right">
                <p className="leading-none text-display-900 paragraph-18">
                  {installmentParcel}x
                </p>
                <p className="mt-4 leading-none text-current paragraph-18">
                  R$
                </p>
              </div>
              <p className="self-end text-[78px] leading-[58px] text-current">
                {installmentValue?.split(',')[0]}
              </p>
              <p className="leading-[20px] text-current paragraph-18">
                ,{installmentValue?.split(',')[1]}
              </p>
            </div>
          </div>
        </div>

        {linkToCheckout && (
          <RetainQueryLinkDefault
            href={linkToCheckout}
            id={`btn-pedir-maquininha-${refID}`}
            className="mt-16 w-full font-bold capitalize bg-newTon-400 btn btn-primary btn-large"
            rel="noreferrer"
          >
            Pedir {machinePopularName}
          </RetainQueryLinkDefault>
        )}
      </div>
      <div className="flex flex-col md:flex-1">
        <div className="flex-1 p-24 mt-auto">
          <Features
            machine={machinePopularName}
            features={machineDetails.features}
          />
        </div>
      </div>
    </div>
  )
}
export default TonMachineCard
